import type { TravelQuery } from "@goblin/domain";
import { useState } from "react";

export type SubmitOpts = { useRealPrices: boolean };

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-goblin-500 focus:outline-none focus:ring-2 focus:ring-goblin-500/30 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100";
const labelCls = "mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300";

type TripType = "roundtrip" | "oneway";

export function QueryBuilder({
  onSubmit,
}: { onSubmit: (q: TravelQuery, opts: SubmitOpts) => void }) {
  const [tripType, setTripType] = useState<TripType>("roundtrip");
  const [origin, setOrigin] = useState("EZE");
  const [destination, setDestination] = useState("CDG");
  const [departMonth, setDepartMonth] = useState("2026-01");
  const [monthPart, setMonthPart] = useState<"early" | "mid" | "late" | "">("late");
  const [durationMin, setDurationMin] = useState(10);
  const [durationMax, setDurationMax] = useState(16);
  const [maxStops, setMaxStops] = useState(1);
  const [extraStopPct, setExtraStopPct] = useState(15);
  const [openTradeoffs, setOpenTradeoffs] = useState(false);
  const [useRealPrices, setUseRealPrices] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const query: TravelQuery = {
      origins: [{ zone: { kind: "airport", iata: origin.toUpperCase() }, weight: 1 }],
      destinations: [{ kind: "airport", iata: destination.toUpperCase() }],
      depart: monthPart
        ? { kind: "partOfMonth", ym: departMonth, part: monthPart }
        : { kind: "month", ym: departMonth },
      returnLeg:
        tripType === "oneway"
          ? { kind: "none" }
          : { kind: "durationDays", minDays: durationMin, maxDays: durationMax },
      pax: { adults: 1, children: 0, infants: 0 },
      cabin: "economy",
      tradeoffs: {
        maxStops,
        extraStopMinSavingsPct: extraStopPct / 100,
        allowOvernightLayover: false,
        originSwitchMinSavingsPct: 0.1,
        cabinDowngradeOk: false,
        preferAlliances: [],
      },
      hardFilters: {
        excludedAirlines: [],
        excludedAirports: [],
        minConnectionMinutes: 60,
      },
    };
    onSubmit(query, { useRealPrices });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Trip type — ida y vuelta vs solo ida */}
      <fieldset className="inline-flex rounded-md border border-slate-300 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-800">
        <legend className="sr-only">Tipo de viaje</legend>
        {(
          [
            { key: "roundtrip", label: "Ida y vuelta" },
            { key: "oneway", label: "Solo ida" },
          ] as { key: TripType; label: string }[]
        ).map((t) => (
          <label
            key={t.key}
            className={`cursor-pointer rounded px-3 py-1.5 text-xs font-medium transition-colors ${
              tripType === t.key
                ? "bg-goblin-500 text-white"
                : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
            }`}
          >
            <input
              type="radio"
              name="trip-type"
              value={t.key}
              checked={tripType === t.key}
              onChange={() => setTripType(t.key)}
              className="sr-only"
            />
            {t.label}
          </label>
        ))}
      </fieldset>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="block">
          <span className={labelCls}>Origen (IATA)</span>
          <input
            className={`${inputCls} uppercase`}
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            maxLength={3}
            required
            aria-label="Código IATA del aeropuerto de origen"
          />
        </label>
        <label className="block">
          <span className={labelCls}>Destino (IATA)</span>
          <input
            className={`${inputCls} uppercase`}
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            maxLength={3}
            required
            aria-label="Código IATA del aeropuerto de destino"
          />
        </label>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="block">
          <span className={labelCls}>Mes de salida</span>
          <input
            type="month"
            className={inputCls}
            value={departMonth}
            onChange={(e) => setDepartMonth(e.target.value)}
            required
          />
        </label>
        <label className="block">
          <span className={labelCls}>Parte del mes</span>
          <select
            className={inputCls}
            value={monthPart}
            onChange={(e) => setMonthPart(e.target.value as typeof monthPart)}
          >
            <option value="">cualquiera</option>
            <option value="early">principios</option>
            <option value="mid">mediados</option>
            <option value="late">fines</option>
          </select>
        </label>
      </div>

      {tripType === "roundtrip" && (
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className={labelCls}>Duración mínima (días)</span>
            <input
              type="number"
              min={1}
              max={365}
              className={inputCls}
              value={durationMin}
              onChange={(e) => setDurationMin(Number(e.target.value))}
            />
          </label>
          <label className="block">
            <span className={labelCls}>Duración máxima (días)</span>
            <input
              type="number"
              min={1}
              max={365}
              className={inputCls}
              value={durationMax}
              onChange={(e) => setDurationMax(Number(e.target.value))}
            />
          </label>
        </div>
      )}

      <div>
        <button
          type="button"
          className="text-sm font-medium text-goblin-700 hover:text-goblin-900 dark:text-goblin-500 dark:hover:text-goblin-400"
          onClick={() => setOpenTradeoffs((o) => !o)}
          aria-expanded={openTradeoffs}
        >
          {openTradeoffs ? "▼" : "▶"} Concesiones (trade-offs)
        </button>
        {openTradeoffs && (
          <div className="mt-3 space-y-4 rounded-md border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
            <label className="block">
              <span className={labelCls}>
                Máximo de escalas: <strong>{maxStops}</strong>
              </span>
              <input
                type="range"
                min={0}
                max={2}
                step={1}
                value={maxStops}
                onChange={(e) => setMaxStops(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>0 (solo directos)</span>
                <span>1</span>
                <span>2</span>
              </div>
            </label>
            <label className="block">
              <span className={labelCls}>
                Acepto escala extra solo si ahorra al menos <strong>{extraStopPct}%</strong>
              </span>
              <input
                type="range"
                min={0}
                max={50}
                step={5}
                value={extraStopPct}
                onChange={(e) => setExtraStopPct(Number(e.target.value))}
                className="w-full"
              />
            </label>
          </div>
        )}
      </div>

      {/* Precios reales (SerpAPI) */}
      <label className="flex cursor-pointer items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/40 dark:bg-amber-950/20">
        <input
          type="checkbox"
          checked={useRealPrices}
          onChange={(e) => setUseRealPrices(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-goblin-500"
        />
        <span className="text-sm text-slate-700 dark:text-slate-300">
          <strong>Precios reales (SerpAPI)</strong> — consume 1 de las 100 búsquedas/mes del free
          tier. Sin marcar, el motor devuelve ofertas mock para probar el pipeline, trade-offs y UI
          sin costo.
        </span>
      </label>

      <button
        type="submit"
        className="rounded-md bg-goblin-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-goblin-700 focus:outline-none focus:ring-2 focus:ring-goblin-500/50"
      >
        Buscar vuelos
      </button>
    </form>
  );
}
